import { Controller } from "@hotwired/stimulus";

/**
 * News feed — the photos and the videos of a post in full screen, as a
 * carousel: one in front, arrows and swipe to move, a rail of thumbnails
 * below that shows them all and jumps to the one clicked, a button to
 * download the one in front. Escape or the cross
 * to close, focus given back to the tile that was opened so the reader is
 * where they were. A video starts when it is reached and stops when it is left.
 */
export default class extends Controller {
  static targets = ["source"];
  static values = { close: String, previous: String, next: String, download: String };

  open(event) {
    this.opener = event.currentTarget;
    this.index = Number(event.params.index) || 0;
    this.build();
    this.show();
  }

  build() {
    this.overlay = document.createElement("div");
    this.overlay.className = "ds-newslightbox";
    this.overlay.setAttribute("role", "dialog");
    this.overlay.setAttribute("aria-modal", "true");
    this.overlay.innerHTML = `
      <span class="ds-newslightbox__count" aria-live="polite"></span>
      <a class="ds-newslightbox__btn ds-newslightbox__btn--download" download aria-label="${this.downloadValue}" title="${this.downloadValue}"><i data-lucide="download"></i></a>
      <button type="button" class="ds-newslightbox__btn ds-newslightbox__btn--close" aria-label="${this.closeValue}"><i data-lucide="x"></i></button>
      <button type="button" class="ds-newslightbox__btn ds-newslightbox__btn--prev" aria-label="${this.previousValue}"><i data-lucide="chevron-left"></i></button>
      <div class="ds-newslightbox__stage"></div>
      <button type="button" class="ds-newslightbox__btn ds-newslightbox__btn--next" aria-label="${this.nextValue}"><i data-lucide="chevron-right"></i></button>
      <div class="ds-newslightbox__foot">
        <span class="ds-newslightbox__caption"></span>
        <div class="ds-newslightbox__rail"></div>
      </div>`;
    this.buildRail();
    this.overlay.querySelector(".ds-newslightbox__btn--close").addEventListener("click", () => this.close());
    this.overlay.querySelector(".ds-newslightbox__btn--prev").addEventListener("click", () => this.move(-1));
    this.overlay.querySelector(".ds-newslightbox__btn--next").addEventListener("click", () => this.move(1));
    this.overlay.addEventListener("click", (event) => {
      if (event.target === this.overlay || event.target.classList.contains("ds-newslightbox__stage")) this.close();
      const thumb = event.target.closest(".ds-newslightbox__thumb");
      if (thumb) this.goTo(Number(thumb.dataset.index));
    });
    this.overlay.addEventListener("touchstart", (event) => { this.touchX = event.touches[0].clientX; }, { passive: true });
    this.overlay.addEventListener("touchend", (event) => {
      const delta = event.changedTouches[0].clientX - this.touchX;
      if (Math.abs(delta) > 48) this.move(delta < 0 ? 1 : -1);
    });
    this.onKey = (event) => {
      if (event.key === "Escape") this.close();
      // The arrows belong to a video that has the focus: they seek in it.
      if (event.target instanceof HTMLVideoElement) return;
      if (event.key === "ArrowLeft") this.move(-1);
      if (event.key === "ArrowRight") this.move(1);
    };
    document.addEventListener("keydown", this.onKey);
    document.body.appendChild(this.overlay);
    document.body.style.overflow = "hidden";
    window.g4sRenderIcons?.();
    this.overlay.querySelector(".ds-newslightbox__btn--close").focus();
  }

  // One thumbnail per photo or video; none when there is a single one.
  buildRail() {
    const rail = this.overlay.querySelector(".ds-newslightbox__rail");
    if (this.sourceTargets.length < 2) { rail.hidden = true; return; }
    this.sourceTargets.forEach((source, index) => {
      const thumb = document.createElement("button");
      thumb.type = "button";
      thumb.className = "ds-newslightbox__thumb";
      thumb.dataset.index = index;
      thumb.setAttribute("aria-label", source.dataset.alt || `${index + 1} / ${this.sourceTargets.length}`);
      if (source.dataset.thumb) {
        const picture = document.createElement("img");
        picture.src = source.dataset.thumb;
        picture.alt = "";
        thumb.appendChild(picture);
      }
      if (source.dataset.kind === "video") thumb.insertAdjacentHTML("beforeend", '<i data-lucide="play"></i>');
      rail.appendChild(thumb);
    });
  }

  show() {
    const source = this.sourceTargets[this.index];
    const single = this.sourceTargets.length < 2;
    this.overlay.querySelector(".ds-newslightbox__stage").replaceChildren(this.media(source));
    this.overlay.querySelector(".ds-newslightbox__caption").textContent = source.dataset.caption || "";
    this.overlay.querySelector(".ds-newslightbox__btn--download").href = source.dataset.download || source.dataset.src;
    this.overlay.querySelector(".ds-newslightbox__count").textContent = single ? "" : `${this.index + 1} / ${this.sourceTargets.length}`;
    this.overlay.querySelectorAll(".ds-newslightbox__btn--prev, .ds-newslightbox__btn--next").forEach((button) => { button.hidden = single; });
    this.overlay.querySelectorAll(".ds-newslightbox__thumb").forEach((thumb, index) => {
      const current = index === this.index;
      thumb.classList.toggle("is-current", current);
      thumb.setAttribute("aria-current", current ? "true" : "false");
      if (current) thumb.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
    });
  }

  media(source) {
    if (source.dataset.kind === "video") {
      const video = document.createElement("video");
      video.className = "ds-newslightbox__media";
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      video.src = source.dataset.src;
      if (source.dataset.alt) video.setAttribute("aria-label", source.dataset.alt);
      return video;
    }
    const image = document.createElement("img");
    image.className = "ds-newslightbox__media";
    image.src = source.dataset.src;
    image.alt = source.dataset.alt || "";
    return image;
  }

  move(step) {
    const total = this.sourceTargets.length;
    this.index = (this.index + step + total) % total;
    this.show();
  }

  goTo(index) {
    if (index === this.index) return;
    this.index = index;
    this.show();
  }

  close() {
    document.removeEventListener("keydown", this.onKey);
    document.body.style.overflow = "";
    this.overlay?.querySelector("video")?.pause();
    this.overlay?.remove();
    this.opener?.focus();
  }

  disconnect() {
    if (this.overlay?.isConnected) this.close();
  }
}
